import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

// Theme foundation (Slice 1). Light / Dark / Auto with OS-following auto,
// localStorage persistence, and live OS-appearance flipping. Frontend-only,
// additive. The palettes already live in index.css (:root + .dark) and Tailwind
// is configured with darkMode: ["class"]; this provider just toggles the
// `.dark` class on <html> and keeps document.style.colorScheme in sync so
// native form controls / scrollbars match.
//
// The no-FOUC inline script in public/index.html MUST mirror this module's
// resolution exactly (same key, same default, same DOM mutations) so there's
// no white flash on load. Keep them in sync.

export const THEME_STORAGE_KEY = 'df-theme';
const VALID_CHOICES = ['light', 'dark', 'auto'];
const MEDIA_QUERY = '(prefers-color-scheme: dark)';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Read the persisted choice; default to 'auto'. localStorage can throw in
// sandboxed iframes / private mode, so guard it.
const readStoredChoice = () => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && VALID_CHOICES.includes(stored)) return stored;
  } catch {
    /* localStorage unavailable */
  }
  return 'auto';
};

const systemPrefersDark = () => {
  try {
    return window.matchMedia(MEDIA_QUERY).matches;
  } catch {
    return false;
  }
};

// Map a choice (light|dark|auto) to the effective mode (light|dark).
const resolveEffective = (choice) => {
  if (choice === 'dark') return 'dark';
  if (choice === 'light') return 'light';
  return systemPrefersDark() ? 'dark' : 'light';
};

// Apply the effective mode to the document: toggle the `.dark` class (Tailwind
// darkMode:["class"]) and set colorScheme so native UA controls match.
const applyEffective = (effective) => {
  const root = document.documentElement;
  if (effective === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  root.style.colorScheme = effective;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readStoredChoice);

  // Apply on mount and whenever the choice changes.
  useEffect(() => {
    applyEffective(resolveEffective(theme));
  }, [theme]);

  // In 'auto', subscribe to OS-appearance changes so the suite flips live when
  // the user's computer toggles light/dark — the "shift with my computer"
  // behavior. Only active while the choice is 'auto'.
  useEffect(() => {
    if (theme !== 'auto') return undefined;
    let mql;
    try {
      mql = window.matchMedia(MEDIA_QUERY);
    } catch {
      return undefined;
    }
    const onChange = (e) => applyEffective(e.matches ? 'dark' : 'light');
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((choice) => {
    if (!VALID_CHOICES.includes(choice)) return;
    try {
      localStorage.setItem(THEME_STORAGE_KEY, choice);
    } catch {
      /* persistence best-effort */
    }
    setThemeState(choice);
  }, []);

  const value = {
    theme,
    setTheme,
    resolvedTheme: resolveEffective(theme),
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
